import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { OverlayContextProvider } from '../../context';
import { AfterCookieBannerContextProvider } from '../../context/afterCookieBannerContext';
import { useLocaleContext } from '../../context/localeContext';
import { useMasterLayoutContext } from '../../context/masterLayoutContext';
import { Footer } from '../footer/Footer';
import { GeoIpPopUp } from '../geoIp';
import { Header } from '../header/Header';
import { SkipMainContentCTA } from '../skipMainContent';
import { FocusReset, Main } from './MasterLayout.styles';

export type MasterLayoutProps = {
  children: ReactNode;
  hideFooter?: boolean;
};

export function MasterLayout({ children, hideFooter }: MasterLayoutProps) {
  const router = useRouter();
  const { locale } = useLocaleContext();

  const { masterLayoutData, navigationHierarchyData, masterLayoutHandlebarComponents } = useMasterLayoutContext();
  const { headerComponent, footerComponent, emptyBasketComponent } = {
    ...masterLayoutData,
  };

  const masterLayoutContext = {
    context: {
      hierarchyData: navigationHierarchyData,
      locale,
    },
  };

  const focusResetRef = useRef<HTMLDivElement>(null);

  const focusReset = () => {
    focusResetRef.current?.focus();
  };

  useEffect(() => {
    focusReset();
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      focusReset();
      router?.events?.off('routeChangeComplete', handleRouteChange);
    };

    const handleClick = (event: Event) => {
      const element = (event.target as HTMLElement).closest('a');
      if (document.querySelector('header')?.contains(element) || document.querySelector('footer')?.contains(element))
        router?.events?.on('routeChangeComplete', handleRouteChange);
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [router?.events]);

  return (
    <OverlayContextProvider>
      <AfterCookieBannerContextProvider>
        <FocusReset ref={focusResetRef} tabIndex={-1} />
        <SkipMainContentCTA />
        <Header
          context={masterLayoutContext}
          headerData={headerComponent}
          emptyBasketComponent={emptyBasketComponent}
          handlebarComponents={masterLayoutHandlebarComponents}
        />
        <Main id="main-content">{children}</Main>
        {!hideFooter && (
          <Footer
            footerData={footerComponent}
            handlebarComponents={masterLayoutHandlebarComponents}
            focusReset={focusReset}
          />
        )}
        <GeoIpPopUp />
      </AfterCookieBannerContextProvider>
    </OverlayContextProvider>
  );
}
